import { alpha } from '@mui/material';

import type { SxProps, Theme } from '../../themes/themes';

export const getStyles = (theme: Theme): { [key: string]: SxProps } => {
  const headLayoutFullPage = {
    backgroundColor: { xs: theme.palette.primary['main'], md: 'transparent' },
    paddingX: { xs: 5, sm: 5, md: 0, lg: 0 },
    '.MuiTypography-root': { color: { xs: 'white', md: 'inherit' } },
    '.PortalsOpener-head-headline': { color: { xs: 'white', md: 'inherit' } },
    '.PortalsOpener-head-overline': {
      color: { xs: 'white', md: 'text.overline' },
      marginBottom: { md: theme.spacing(5), lg: theme.spacing(6) },
    },
  };

  const headLayoutNormal = {
    position: { xs: 'sticky', sm: 'static' },
    top: 0,
    backgroundColor: { xs: theme.palette.primary['main'], sm: 'transparent' },
    paddingX: { xs: 5, sm: 0 },
    paddingTop: { xs: 6, sm: 0 },
    paddingBottom: { xs: 6, sm: 0 },
    '.MuiTypography-root': { color: { xs: 'white', sm: 'inherit' } },
    '.PortalsOpener-head-headline': { color: { xs: 'white', sm: 'inherit' } },
    '.PortalsOpener-head-overline': {
      color: { xs: 'white', sm: 'text.overline' },
      marginBottom: { sm: theme.spacing(5), lg: theme.spacing(6) },
    },
  };

  const contentLayoutFullPage = {
    display: { xs: 'contents', sm: 'contents', md: 'flex', lg: 'flex' },
    rowGap: { xs: 9, sm: 0, md: 0, lg: 0 },
    position: 'relative',
    width: '100%',
    marginTop: { md: theme.spacing(10) },
  };

  const contentLayoutNormal = {
    display: { xs: 'contents', sm: 'flex', md: 'flex', lg: 'flex' },
    rowGap: { xs: 0, sm: 0, md: 0, lg: 0 },
    gridArea: 'content',
    alignSelf: 'center',
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
      marginTop: 0,
      marginBottom: 0,
      paddingLeft: theme.spacing(8),
      paddingRight: theme.spacing(8),
    },
  };

  const bodyLayoutFullPage = {
    rowGap: { xs: 4, md: 0 },
    marginTop: { xs: 8, md: 0 },
    marginBottom: { xs: 0 },
    marginLeft: { xs: 5, sm: 8, md: 0 },
    marginRight: { xs: 5, sm: 8, md: 0 },
    '.MuiTypography-root': {
      marginBottom: {
        xs: theme.spacing(4),
        sm: theme.spacing(5),
        lg: theme.spacing(6),
      },
    },
    '.MuiSvgIcon-root': {
      color: {
        xs: theme.palette.green['main'],
        md: theme.palette.green['contrastText'],
      },
    },
    hr: {
      display: 'none',
    },
  };

  const bodyLayoutNormal = {
    rowGap: { xs: 4, sm: 0 },
    marginTop: { xs: 5, md: 6 },
    marginBottom: { xs: 0 },

    '.MuiTypography-root': {
      marginBottom: {
        xs: theme.spacing(4),
        sm: theme.spacing(5),
        lg: theme.spacing(6),
      },
    },
    hr: {
      display: 'none',
    },
  };

  const foot = {
    padding: { xs: 5, sm: 5, md: 5, lg: 6 },
    rowGap: { xs: 5, sm: 5, md: 6, lg: 6 },
    alignItems: { xs: 'inherit', md: 'flex-start' },
  };

  const mediaImage = {
    '& img': {
      objectFit: 'cover',
      width: '100%',
      height: '100%',
      display: 'block',
      // Help for showing the alt text on broken images in accessible way
      lineHeight: 1.5,
      fontSize: '16px',
      fontFamily: 'monospace',
      minHeight: { md: '512px', lg: '640px' },
      maxHeight: { xs: '50vw', md: '720px', lg: '920px' },
    },
  };

  const innerMedia = {
    backgroundColor: '#000',
    color: '#fff',
  };

  const productPageStyles = {
    head: {
      ...headLayoutFullPage,
      maxWidth: { xs: '100%', sm: '100%', md: '50%', lg: '50%' },
      paddingY: { xs: 6 },
    },
    container: {
      gridTemplateColumns: '1fr',
      gridTemplateAreas: `'head'
           'media'
           'actions'
           'body'
           'foot'`,
      [theme.breakpoints.up('md')]: {
        marginLeft: 'auto',
        marginRight: 'auto',
        gridTemplateColumns: `1fr minmax(100%, ${theme.breakpoints.values.xl}px) 1fr`,
        gridTemplateAreas: `'mediaLeft content mediaRight'`,
      },
    },
    content: {
      ...contentLayoutFullPage,
      color: theme.palette.primary['contrastText'],
      '& .PortalsOpener-body': {
        color: theme.palette.text['secondary'],
        maxWidth: '100%',
        [theme.breakpoints.up('md')]: {
          color: theme.palette.primary['contrastText'],
          maxWidth: '50%',
        },
        '& .MuiListItemAvatar-root': {
          paddingLeft: 0,
        },
      },
    },
    media: {
      lineHeight: 0,
      padding: 0,

      [theme.breakpoints.up('md')]: {
        gridRow: '1',
        gridColumnStart: 'mediaLeft',
        gridColumnEnd: 'mediaRight',
      },
      ...mediaImage,
    },
    innerMedia: {
      ...innerMedia,
      aspectRatio: '2 / 1',
      [theme.breakpoints.up('md')]: {
        aspectRatio: 'unset',
      },
    },
    body: {
      ...bodyLayoutFullPage,
    },
    foot: {
      ...foot,
      marginLeft: { xs: 5, sm: 8, md: 0 },
      marginRight: { xs: 5, sm: 8, md: 0 },
    },
    actions: {
      gridColumn: { xs: '1 / 3', sm: '1 / 3', md: '2 / 2', lg: '2 / 2' },
      gridRow: { xs: 3, sm: 3, md: 1, lg: 1 },
      alignSelf: 'end',
      textAlign: 'center',
      width: '100%',
      '& .MuiDivider-root': {
        margin: `0 ${theme.spacing(5)}`,
        marginBottom: { xs: 5, md: 9 },
      },
    },
  };

  return {
    head: {
      productPage: productPageStyles.head,
      productPageSmall: productPageStyles.head,
      servicePage: {
        ...headLayoutFullPage,
        paddingTop: theme.spacing(6),
        paddingBottom: theme.spacing(6),
      },
      serviceAndCampaignPage: {
        ...headLayoutFullPage,
        paddingTop: { xs: 5, md: 0 },
        paddingBottom: { xs: 6, lg: 9 },
        gap: { xs: 2, md: 0 },
      },
      deviceImageLeft: {
        ...headLayoutNormal,
      },
      deviceImageRight: {
        ...headLayoutNormal,
      },
      homeImageLeft: {
        ...headLayoutNormal,
      },
      homeImageRight: {
        ...headLayoutNormal,
      },
    },
    container: {
      productPage: productPageStyles.container,
      productPageSmall: productPageStyles.container,
      servicePage: {
        gridTemplateColumns: '1fr',
        gridTemplateAreas: `'head'
             'media'
             'body'
             'actions'
             'foot'`,
        [theme.breakpoints.up('md')]: {
          marginLeft: 'auto',
          marginRight: 'auto',
          maxWidth: theme.breakpoints.values.xl,
          gridTemplateColumns: '1fr 1fr',
          gridTemplateAreas: `'media media'
             'actions actions'`,
        },
      },
      serviceAndCampaignPage: {
        gridTemplateColumns: '1fr',
        gridTemplateAreas: `'head'
             'media'
             'actions'
             'body'             
             'foot'`,
        [theme.breakpoints.up('md')]: {
          background: theme.palette.grey[50],
          columnGap: theme.spacing(6),
          gridTemplateColumns: '1fr 1fr',
          gridTemplateAreas: `'media content'
             'actions actions'`,
        },
        [theme.breakpoints.up('lg')]: {
          columnGap: theme.spacing(9),
        },
      },
      deviceImageLeft: {
        gridTemplateColumns: '1fr',
        gridTemplateAreas: `'head'
             'media'
             'body'
             'actions'
             'foot'`,
        marginBottom: theme.spacing(8),
        [theme.breakpoints.up('sm')]: {
          gridTemplateColumns: '1fr 1fr',
          gridTemplateAreas: `'media content'
             'actions actions'`,
          marginBottom: theme.spacing(10),
        },
      },
      deviceImageRight: {
        gridTemplateColumns: '1fr',
        gridTemplateAreas: `'head'
             'media'
             'body'
             'actions'
             'foot'`,
        marginBottom: theme.spacing(8),
        [theme.breakpoints.up('sm')]: {
          gridTemplateColumns: '1fr 1fr',
          gridTemplateAreas: `'content media'
             'actions actions'`,
          marginBottom: theme.spacing(10),
        },
      },
      homeImageLeft: {
        gridTemplateColumns: '1fr',
        gridTemplateAreas: `'head'
             'media'
             'body'
             'actions'
             'foot'`,
        [theme.breakpoints.up('sm')]: {
          columnGap: theme.spacing(6),
          gridTemplateColumns: '1fr 1fr',
          gridTemplateAreas: `'media content'
             'actions actions'`,
        },
        [theme.breakpoints.up('lg')]: {
          columnGap: theme.spacing(9),
        },
      },
      homeImageRight: {
        gridTemplateColumns: '1fr',
        gridTemplateAreas: `'head'
             'media'
             'body'
             'actions'
             'foot'`,
        [theme.breakpoints.up('sm')]: {
          columnGap: theme.spacing(6),
          gridTemplateColumns: '1fr 1fr',
          gridTemplateAreas: `'content media'
             'actions actions'`,
        },
        [theme.breakpoints.up('lg')]: {
          columnGap: theme.spacing(9),
        },
      },
    },
    content: {
      productPage: productPageStyles.content,
      productPageSmall: productPageStyles.content,
      servicePage: {
        ...contentLayoutFullPage,
        gridColumn: 1,
        gridRow: 1,
        rowGap: '0',
        color: theme.palette.primary['contrastText'],
        '& .PortalsOpener-body': {
          color: theme.palette.text['secondary'],
          [theme.breakpoints.up('md')]: {
            color: theme.palette.primary['contrastText'],
          },
        },
      },
      serviceAndCampaignPage: {
        ...contentLayoutFullPage,
        marginLeft: { xs: 0, md: -5, lg: -6 },
        marginTop: { md: 10, lg: 12 },
      },
      deviceImageLeft: {
        ...contentLayoutNormal,
      },
      deviceImageRight: {
        ...contentLayoutNormal,
      },
      homeImageLeft: {
        ...contentLayoutNormal,
        '& .PortalsOpener-body': {
          color: theme.palette.text['secondary'],
        },
      },
      homeImageRight: {
        ...contentLayoutNormal,
        '& .PortalsOpener-body': {
          color: theme.palette.text['secondary'],
        },
      },
    },

    media: {
      productPage: productPageStyles.media,
      productPageSmall: {
        ...productPageStyles.media,
        '& img': {
          ...productPageStyles.media['& img'],
          maxHeight: { xs: '50vw', md: '512px', lg: '640px' },
        },
      },
      servicePage: {
        lineHeight: 0,
        padding: 0,
        ...mediaImage,
      },
      serviceAndCampaignPage: {
        background: theme.palette.grey[50],
        lineHeight: 0,
        padding: { xs: 5, sm: 8, md: 12 },
        paddingRight: { xs: 5, sm: 8, md: 0 },
        alignSelf: 'center',
        '& img': {
          ...mediaImage['& img'],
          objectFit: 'contain',
          objectPosition: 'center',
          [theme.breakpoints.up('md')]: {
            objectPosition: 'center right',
          },
        },
      },
      deviceImageLeft: {
        lineHeight: 0,
        [theme.breakpoints.up('sm')]: {
          padding: 12,
        },
        ...mediaImage,
      },
      deviceImageRight: {
        lineHeight: 0,
        [theme.breakpoints.up('sm')]: {
          padding: 12,
        },
        ...mediaImage,
      },
      homeImageLeft: {
        lineHeight: 0,
        padding: 0,
        ...mediaImage,
      },
      homeImageRight: {
        lineHeight: 0,
        padding: 0,
        ...mediaImage,
      },
    },

    innerMedia: {
      productPage: productPageStyles.innerMedia,
      productPageSmall: productPageStyles.innerMedia,
      servicePage: {
        ...innerMedia,
      },
      serviceAndCampaignPage: {
        color: '#000',
      },
      deviceImageLeft: {
        ...innerMedia,
      },
      deviceImageRight: {
        ...innerMedia,
      },
      homeImageLeft: {
        ...innerMedia,
      },
      homeImageRight: {
        ...innerMedia,
      },
    },

    body: {
      productPage: productPageStyles.body,
      productPageSmall: productPageStyles.body,
      servicePage: {
        ...bodyLayoutFullPage,
      },
      serviceAndCampaignPage: {
        marginTop: { xs: 8, md: 0 },
        marginX: { xs: 5, sm: 8, md: 0 },
      },
      deviceImageLeft: {
        ...bodyLayoutNormal,
      },
      deviceImageRight: {
        ...bodyLayoutNormal,
      },
      homeImageLeft: {
        ...bodyLayoutNormal,
      },
      homeImageRight: {
        ...bodyLayoutNormal,
      },
    },

    foot: {
      productPage: productPageStyles.foot,
      productPageSmall: productPageStyles.foot,
      servicePage: {
        ...foot,
        marginLeft: { xs: 5, sm: 8, md: 0 },
        marginRight: { xs: 5, sm: 8, md: 0 },
      },
      serviceAndCampaignPage: {
        alignItems: { xs: 'inherit', md: 'flex-start' },
        paddingX: { xs: 5, sm: 5, md: 6, lg: 6 },
        marginLeft: { xs: 5, sm: 8, md: 0 },
        marginRight: { xs: 5, sm: 8, md: 0 },
        rowGap: theme.spacing(8),
        marginTop: { xs: 5, lg: 6 },
      },
      deviceImageLeft: {
        ...foot,
      },
      deviceImageRight: {
        ...foot,
      },
      homeImageLeft: {
        ...foot,
      },
      homeImageRight: {
        ...foot,
      },
    },

    actions: {
      productPage: productPageStyles.actions,
      productPageSmall: productPageStyles.actions,
      servicePage: {
        justifySelf: 'center',
        backgroundColor: 'white',
        marginTop: 0,
        marginBottom: 0,
        [theme.breakpoints.up('md')]: {
          transform: 'translateY(-175px)',
          marginBottom: '-175px',
          boxShadow: `0px 2px 3px ${alpha(theme.palette.secondary['main'], 0.1)}`,
        },
      },
      serviceAndCampaignPage: {
        gridColumn: '1 / 3',
      },
      deviceImageLeft: {},
      deviceImageRight: {},
      homeImageLeft: {},
      homeImageRight: {},
    },
  };
};
